import { z } from "zod";

import { GroupIds } from "@/shared/group-configs/groups";

export const EVAL_RUN_SPEC_VERSION = 2;
export const MAX_EVAL_ATTEMPTS_PER_RUN = 200;
export const MAX_BACKEND_EVAL_ATTEMPTS_PER_RUN = 200;
export const MAX_BACKEND_ELIGIBILITY_CASE_ATTEMPTS_PER_RUN = 25;
export const EVAL_SUITES = ["scenarios", "integration", "backend"] as const;
export const EVAL_AGENT_RUNTIMES = ["cascaded", "slow-thinker", "realtime"] as const;

const safeIdentifierSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:/-]*$/, "contains unsupported characters");
const modelIdentifierSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:/-]*$/, "contains unsupported characters");
const organizationSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9-]*$/, "must be a lowercase organization slug");
const runNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[^\u0000-\u001f\u007f]+$/, "must not contain control characters");

export const evalAgentVariantSchema = z.enum(["cascaded", "integration", "realtime", "slow-thinker"]);
export const evalAgentRuntimeSchema = z.enum(EVAL_AGENT_RUNTIMES);
export const evalExecutionModeSchema = z.enum(["text", "audio"]);
export const evalScenarioIdSchema = safeIdentifierSchema;
export const evalSuiteSchema = z.enum(EVAL_SUITES);

export type EvalAgentVariantName = z.infer<typeof evalAgentVariantSchema>;
export type EvalAgentRuntime = z.infer<typeof evalAgentRuntimeSchema>;
export type EvalExecutionMode = z.infer<typeof evalExecutionModeSchema>;
export type EvalSuite = z.infer<typeof evalSuiteSchema>;

export const evalScenarioSelectionSchema = z
  .object({
    type: z.literal("scenarios"),
    scenarioIds: z.array(evalScenarioIdSchema).min(1).max(500),
  })
  .strict();

export const evalGroupSelectionSchema = z
  .object({
    type: z.literal("group"),
    groupId: z.nativeEnum(GroupIds),
  })
  .strict();

export const evalPackSelectionSchema = z
  .object({
    type: z.literal("pack"),
    packId: evalScenarioIdSchema,
  })
  .strict();

const textExecutionSchema = z
  .object({
    mode: z.literal("text"),
    scenarioConcurrency: z.number().int().min(1).max(20).default(1),
  })
  .strict();

const audioExecutionSchema = z
  .object({
    mode: z.literal("audio"),
    scenarioConcurrency: z.number().int().min(1).max(10).default(1),
    captureAudio: z.boolean().default(true),
    callerTts: z
      .object({
        model: modelIdentifierSchema,
        voice: safeIdentifierSchema.optional(),
      })
      .strict(),
    agentSttModel: modelIdentifierSchema,
    agentTtsModel: modelIdentifierSchema,
    interruptionsEnabled: z.boolean().default(true),
  })
  .strict();

export const evalRunSpecSchema = z
  .object({
    version: z.literal(EVAL_RUN_SPEC_VERSION),
    runId: z.string().uuid(),
    // Optional for version-2 compatibility. New dispatchers persist both fields
    // so the worker can maintain the compact history index without rereading catalogs.
    createdAt: z.string().datetime().optional(),
    scenarioCount: z.number().int().positive().max(MAX_BACKEND_EVAL_ATTEMPTS_PER_RUN).optional(),
    name: runNameSchema,
    requestedBy: z
      .object({
        userId: safeIdentifierSchema,
        email: z.string().email().optional(),
      })
      .strict(),
    source: z
      .object({
        gitSha: z.string().regex(/^[a-f0-9]{40}$/i, "must be a full git SHA"),
        imageDigest: z
          .string()
          .regex(/^sha256:[a-f0-9]{64}$/i)
          .optional(),
      })
      .strict(),
    suite: evalSuiteSchema,
    organization: organizationSchema.optional(),
    selection: z.discriminatedUnion("type", [
      evalScenarioSelectionSchema,
      evalGroupSelectionSchema,
      evalPackSelectionSchema,
    ]),
    attempts: z.number().int().min(1).max(100),
    agentVariant: evalAgentVariantSchema,
    // Optional for version-2 spec compatibility; new dispatchers always persist it explicitly.
    agentRuntime: evalAgentRuntimeSchema.optional(),
    models: z
      .object({
        agent: modelIdentifierSchema,
        provider: safeIdentifierSchema.optional(),
        judge: modelIdentifierSchema,
      })
      .strict(),
    quality: z.boolean().default(false),
    execution: z.discriminatedUnion("mode", [textExecutionSchema, audioExecutionSchema]),
  })
  .strict()
  .superRefine((spec, ctx) => {
    const agentRuntime = spec.agentRuntime ?? getDefaultEvalAgentRuntime(spec.agentVariant);

    if (
      spec.selection.type === "scenarios" &&
      new Set(spec.selection.scenarioIds).size !== spec.selection.scenarioIds.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "scenarioIds must not contain duplicates",
        path: ["selection", "scenarioIds"],
      });
    }

    if (spec.suite === "integration" && spec.attempts !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "integration runs must use exactly one attempt",
        path: ["attempts"],
      });
    }

    if (spec.suite === "integration" && spec.execution.mode !== "text") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "integration runs currently support text execution only",
        path: ["execution", "mode"],
      });
    }

    if (spec.suite === "integration" && spec.agentVariant !== "integration") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "integration runs must use the integration agent variant",
        path: ["agentVariant"],
      });
    }

    if (spec.suite === "scenarios" && spec.agentVariant === "integration") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the integration agent variant requires the integration suite",
        path: ["suite"],
      });
    }

    if (spec.suite === "backend" && spec.agentVariant !== "cascaded") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs use the cascaded variant as their non-agent execution marker",
        path: ["agentVariant"],
      });
    }

    if (spec.suite === "backend" && spec.execution.mode !== "text") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs support text execution only",
        path: ["execution", "mode"],
      });
    }

    if (spec.suite === "backend" && spec.selection.type === "group") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs require a scenario or pack selection",
        path: ["selection"],
      });
    }

    if (spec.suite !== "backend" && spec.selection.type === "pack") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "pack selections require the backend suite",
        path: ["selection"],
      });
    }

    if (spec.suite === "backend" && spec.quality) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "backend runs do not support conversation quality judging",
        path: ["quality"],
      });
    }

    if (spec.agentVariant !== "integration" && agentRuntime !== getDefaultEvalAgentRuntime(spec.agentVariant)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `the ${spec.agentVariant} agent variant requires the ${getDefaultEvalAgentRuntime(spec.agentVariant)} runtime`,
        path: ["agentRuntime"],
      });
    }

    if (spec.selection.type !== "scenarios") return;

    try {
      validateEvalAttemptBudget(spec.selection.scenarioIds, spec.attempts, spec.suite);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: error instanceof Error ? error.message : String(error),
        path: ["attempts"],
      });
    }
  });

export type EvalRunSpec = z.infer<typeof evalRunSpecSchema>;
export type EvalRunSelection = EvalRunSpec["selection"];

/** Backward-compatible runtime for specs written before runtime was separated from authorization variant. */
export function getDefaultEvalAgentRuntime(agentVariant: EvalAgentVariantName): EvalAgentRuntime {
  switch (agentVariant) {
    case "slow-thinker":
      return "slow-thinker";
    case "realtime":
      return "realtime";
    case "cascaded":
    case "integration":
      return "cascaded";
  }
}

/** Validates untrusted run input before it reaches the eval process or environment. */
export function parseEvalRunSpec(input: unknown): EvalRunSpec {
  return evalRunSpecSchema.parse(input);
}

/** Enforces the multiplicative budget after a catalog-based group selection is resolved. */
export function validateEvalAttemptBudget(
  scenarioIds: readonly string[],
  attempts: number,
  suite: EvalSuite = "scenarios"
): void {
  const maxAttempts = suite === "backend" ? MAX_BACKEND_EVAL_ATTEMPTS_PER_RUN : MAX_EVAL_ATTEMPTS_PER_RUN;

  if (suite === "backend") {
    const eligibilityCaseCount = scenarioIds.filter((id) => id.startsWith("backend/check-eligibility/")).length;

    if (eligibilityCaseCount * attempts > MAX_BACKEND_ELIGIBILITY_CASE_ATTEMPTS_PER_RUN) {
      throw new Error(
        `selected eligibility cases * attempts must not exceed ${MAX_BACKEND_ELIGIBILITY_CASE_ATTEMPTS_PER_RUN} per run`
      );
    }
  }

  if (scenarioIds.length * attempts > maxAttempts) {
    throw new Error(`selected scenarios * attempts must not exceed ${maxAttempts} total attempts per run`);
  }
}
